import React, { useRef } from 'react'
import { css, cx } from '@fable/theme'
import { useLocation } from 'react-router-dom'
import { BLOCKS, INLINES, Node } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

import Loader, { LoaderType } from '../../../../components/loader'
import useSlugQuery from '../../../../hooks/useSlugQuery'
import DetailMasthead from '../../components/detail_masthead/DetailMasthead'
import SplitContent from '../../components/split_content/SplitContent'
import { GenresSlider } from './GenresSlider'

import calloutIllustration from '../../../../assets/images/graphics/art-Conversation.webp'

import errorCardModalPayload from '../../../../components/error_card/errorCardModalPayload'
import { GET_GENRE, GET_GENRE_ID } from '../../../../queries'
import { ContentTypes, Genre } from '../../../../types'
import { Color, Align, TextColor } from '@fable/legacy'
import { getSlug } from '../../../../utils'
import { BREAKPOINTS } from '../../../../constants'
import { useActions, useTypedSelector } from '../../../../hooks'
import { FORM_TYPES } from '../../../auth/constants'
import { QuoteIcon } from '../../../../assets/icons/components'
import { PostBookList } from '../../../../components/post_book_list'
import { Button, Text } from '@fable/components'
import { Page } from '../../../../components/Page'

const GenresDetail: React.FC = () => {
  const { openModal } = useActions()
  const location = useLocation()
  const { openAuthModal } = useActions()
  const { isAuthenticated } = useTypedSelector(({ auth }) => auth)
  const modal = useTypedSelector(({ modal }) => modal)
  const slug = getSlug(location.pathname)

  const listRef = useRef<HTMLDivElement>(null)

  const { data, error, loading } = useSlugQuery(
    GET_GENRE_ID,
    slug,
    'genreCollection',
    GET_GENRE
  )

  const contentOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => (
        <div className={cx(styles.paragraph, TextColor.blackSwan)}>
          {children}
        </div>
      ),
      [BLOCKS.HEADING_3]: (node: Node, children: React.ReactNode) => (
        <div className={cx(styles.h3, TextColor.blackSwan)}>{children}</div>
      ),
      [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
        const id = node.data.target.sys.id
        return (
          <PostBookList className={cx(styles.bookList)} id={id} ref={listRef} />
        )
      },
    },
  }

  const scrollToBookList = () => {
    if (!listRef.current) return

    listRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  const renderQuote = () => {
    const { quote, color } = data.genre
    return (
      <div className={cx(styles.section)}>
        <div className={cx(styles.quote, Align.hLeft)}>
          <QuoteIcon color={color} />
          <div className={cx(styles.quoteDetails)}>
            <Text
              type="subhead"
              sizing={{ base: 'M', tabletL: 'L', mobileL: 'XL' }}
            >
              {quote.sentence}
              <br />- {quote.author}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  const renderSkipButton = () => {
    return (
      <div className={cx(styles.section, styles.skipButton)}>
        <Text
          className={css`
            text-decoration: underline;
            cursor: pointer;
          `}
          tag="span"
          type="subhead"
          sizing={{ base: 'XS', mobileL: 'S' }}
          onClick={scrollToBookList}
        >
          Skip to {data.genre.name} book selections
        </Text>
      </div>
    )
  }

  // NB: Uncomment when user can view all genre related books
  // const renderViewAllCTA = () => {
  //   return (
  //     <div className={cx(styles.section, styles.cta)}>
  //       <button className="button border">
  //         View all {data.genre.name} books
  //       </button>
  //     </div>
  //   );
  // };

  const renderJoinCallout = () => {
    const title = 'Join the club!'
    const description =
      'With the Fable app, you can join or start your own online book club. Browse the free book clubs hosted by our favorite bookworms'

    return (
      !isAuthenticated && (
        <div className={cx(styles.section, styles.calloutSection)}>
          <div className={cx(styles.callout)}>
            <div className={cx(styles.calloutDetails)}>
              <Text
                tag="h3"
                className={cx(styles.calloutTitle)}
                type="header"
                sizing={{ base: 'M', mobileL: 'L' }}
              >
                {title}
              </Text>
              <Text
                className={styles.calloutDescription}
                type="body"
                sizing="M"
              >
                {description}
              </Text>
              <Button
                className={css`
                  padding: 0 20px;
                `}
                inverted
                onClick={() =>
                  openAuthModal(FORM_TYPES.SIGN_UP, location.pathname)
                }
              >
                Sign up for free
              </Button>
            </div>
            <img
              className={cx(styles.calloutImage)}
              src={calloutIllustration}
              alt="Join Fable Illustration"
            />
          </div>
        </div>
      )
    )
  }

  const renderContent = () => {
    if (error) {
      if (!modal?.isOpen) {
        const { content, modalType } = errorCardModalPayload()
        openModal(content, modalType)
      }
      return <div />
    }
    if (loading) return <Loader type={LoaderType.fullscreen} />
    if (!data) return null

    const contentType = ContentTypes.genres

    const { name, color, content, description, seoTitleTag, seoDescription } =
      data.genre as Genre

    return (
      <Page
        head={{
          title: name,
          description: description,
          seoTitle: seoTitleTag,
          seoDescription: seoDescription,
          canonical: true,
        }}
        className={cx(styles.wrapper, 'genres-detail detail-page fill fade')}
      >
        <SplitContent>
          <DetailMasthead
            info={data.genre}
            type={contentType}
            key={'split-left'}
            color={color}
          />
          <React.Fragment key={'split-right'}>
            <div className={cx(styles.sections)}>
              {renderQuote()}
              {renderSkipButton()}
              <div className={cx(styles.section, styles.mainContent)}>
                {documentToReactComponents(content.json, contentOptions)}
              </div>
              {/* {renderViewAllCTA()} */}
              <GenresSlider currentGenre={name} />
              {renderJoinCallout()}
            </div>
          </React.Fragment>
        </SplitContent>
      </Page>
    )
  }

  return renderContent()
}

const styles = {
  wrapper: css`
    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      .genre-masthead {
        padding: 90px 0 48px;
        display: block;
      }
    }

    @media only screen and (min-width: 960px) and (max-height: 780px) {
      .page-section {
        height: 721px;
      }
    }
  `,
  sections: css`
    padding-bottom: 32px;
    max-width: 800px;

    @media only screen and (max-width: 961px) {
      margin-top: 52px;
      max-width: none;
    }

    @media only screen and (max-width: 649px) {
      padding-bottom: 0;
    }
  `,
  section: css`
    padding: 0 24px;

    @media only screen and (min-width: 649px) {
      padding: 0 32px;
    }
  `,
  mainContent: css`
    div {
      margin-bottom: 28px;

      a {
        color: ${Color.blackSwan};
        font-weight: 600;
        text-decoration: underline;

        u {
          text-decoration: none;
        }
      }
    }
  `,
  paragraph: css`
    line-height: 25px;
    white-space: pre-wrap;
  `,
  h3: css`
    font-size: 24px !important;
    line-height: 28px !important;
    font-weight: 600;
  `,
  bookList: css`
    @media only screen and (max-width: 648px) {
      width: calc(100% + 48px);
      margin-left: -24px;
    }

    @media only screen and (min-width: 961px) and (max-width: 1248px) {
      grid-template-columns: repeat(1, 1fr);
    }
  `,
  quote: css`
    .fds-ui--icon {
      margin-top: -16px;
      width: 120px;
      height: 120px;
      flex-shrink: 0;
    }

    p {
      padding: 0;
      font-size: 24px !important;
      line-height: 28px !important;
      font-weight: 600;
    }

    @media only screen and (max-width: 648px) {
      .fds-ui--icon {
        margin-top: -4px;
        width: 60px;
        height: 60px;
      }

      p {
        font-size: 18px !important;
        line-height: 25px !important;
      }
    }
  `,
  quoteDetails: css`
    padding-left: 46px;
    max-width: 505px;

    @media only screen and (max-width: 648px) {
      padding-left: 16px;
    }
  `,
  skipButton: css`
    margin: 42px 0 24px;

    button {
      text-decoration: underline;
    }
  `,
  cta: css`
    margin-top: 60px;
  `,
  calloutSection: css`
    @media only screen and (max-width: 649px) {
      padding: 0;
    }
  `,
  callout: css`
    margin-top: 60px;
    padding: 45px 52px;
    background-color: ${Color.fableGreen};
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    @media only screen and (max-width: 649px) {
      margin: 0;
      padding: 40px 24px;
      border-radius: 0;
      flex-direction: column-reverse;
      align-items: center;
    }
  `,
  calloutDetails: css`
    width: calc(100% - 200px - 24px);

    button {
      width: auto;
      background: ${Color.pinkMoon} !important;
      border-color: transparent !important;
      color: ${Color.blackSwan} !important;

      @media only screen and (max-width: 649px) {
        margin: 0 auto;
      }
    }

    button:hover {
      background: ${Color.lavenderBlush} !important;
    }

    @media only screen and (max-width: 649px) {
      max-width: 318px;
      width: 100%;
      text-align: center;
    }
  `,
  calloutTitle: css`
    color: ${Color.pinkMoon};
  `,
  calloutDescription: css`
    margin: 10px 0 42px;
    color: ${Color.whiteFang};
    font-size: 16px !important;
    line-height: 24px !important;

    @media only screen and (max-width: 649px) {
      margin: 20px 0;
      line-height: 20px !important;
      letter-spacing: 0.3px;
    }
  `,
  calloutImage: css`
    margin-left: 24px;
    display: block;
    width: 200px;
    position: relative;
    top: -25px;

    @media only screen and (max-width: 649px) {
      margin-left: 0;
      margin-bottom: 32px;
      top: 0;
      width: 176px;
    }
  `,
}

export default GenresDetail
